import React, { useCallback } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { ChatModal } from 'components/Chat/ChatModal/ChatModal'
import { SkeletonBlock } from 'components/NewTaskDashboard/common'
import { BaseModalV3, CloseButton } from 'components/ui'
import { RootState } from 'constants/interfaces'
import { setNotificationChatModal } from 'redux/actions/feed'

const ChatModalSkeleton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <BaseModalV3
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" py="10px">
        <Flex alignItems="center" flex="1" minW="0" style={{ gap: '12px' }}>
          <SkeletonBlock h="42px" w="42px" borderRadius="999px" />
          <Box flex="1" minW="0">
            <SkeletonBlock h="14px" w="44%" borderRadius="4px" />
            <SkeletonBlock h="11px" w="28%" borderRadius="4px" mt="8px" />
          </Box>
        </Flex>
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Box px="20px" py="16px">
        <Flex alignItems="center" mb="18px" style={{ gap: '12px' }}>
          <SkeletonBlock h="34px" w="34px" borderRadius="999px" />
          <SkeletonBlock h="44px" w="58%" borderRadius="14px" />
        </Flex>
        <Flex alignItems="center" justifyContent="flex-end" mb="18px" style={{ gap: '12px' }}>
          <SkeletonBlock h="52px" w="62%" borderRadius="14px" />
          <SkeletonBlock h="34px" w="34px" borderRadius="999px" />
        </Flex>
        <Flex alignItems="center" mb="18px" style={{ gap: '12px' }}>
          <SkeletonBlock h="34px" w="34px" borderRadius="999px" />
          <SkeletonBlock h="76px" w="68%" borderRadius="14px" />
        </Flex>
      </Box>
    }
    footer={<SkeletonBlock h="42px" w="100%" borderRadius="10px" />}
    bodyProps={{ style: { background: 'white', height: '70vh' } }}
  />
)

const ContentUnavailable: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <BaseModalV3
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" py="10px">
        <Text fontSize="16px" fontWeight={700} color="#0F172A">
          Notification
        </Text>
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Flex h="100%" minH="320px" alignItems="center" justifyContent="center" flexDirection="column" textAlign="center">
        <Text fontSize="15px" fontWeight={700} color="#0F172A">
          Content is no longer available
        </Text>
        <Text fontSize="13px" color="#64748B" mt="8px" maxW="320px">
          It may have been deleted, moved, or you may no longer have access.
        </Text>
      </Flex>
    }
    footer={<Box />}
    bodyProps={{ style: { background: 'white', height: '70vh' } }}
  />
)

export const NotificationChatModalHost: React.FC = () => {
  const dispatch = useDispatch()
  const chatId = useSelector((state: RootState) => state.feed.notificationChatModalID)
  const target = useSelector((state: RootState) => state.feed.notificationChatModalTarget)
  const error = useSelector((state: RootState) => state.feed.notificationChatModalError)
  const chat = useSelector((state: RootState) => state.feed.chats.find((item) => item.post_id === chatId))

  const handleClose = useCallback(() => {
    dispatch(setNotificationChatModal(null))
  }, [dispatch])

  if (!chatId) return null
  if (error) return <ContentUnavailable onClose={handleClose} />
  if (!chat) return <ChatModalSkeleton onClose={handleClose} />

  return (
    <ChatModal
      isOpen={true}
      onClose={handleClose}
      chat={chat}
      targetParentCommentId={target?.targetParentCommentId}
      targetCommentId={target?.targetCommentId}
    />
  )
}
